/**
 * x-tooltip — 커스텀 툴팁 디렉티브 (2026-10-06 내부 재작성, API 유지).
 *
 * 사용: <span x-data x-tooltip.right="텍스트">…</span>   모디파이어 .top(기본)/.bottom/.left/.right/.noarrow
 *       Thymeleaf: th:attr="x-tooltip.right=${item.info6}"
 *       정렬(2026-10-13, top/bottom 전용): .start = 호스트에서 오른쪽으로 펼침(화살표가 왼쪽 끝 ARROW_INSET 에),
 *                                          .end = 왼쪽으로 펼침. 생략 시 중앙 정렬. 예) x-tooltip.bottom.start.tonal
 *       색 변형(2026-10-13): .tonal = 블루 tonal 바탕(style.css [data-variant="tonal"], --ws-primary-tonal). 생략 시 기본 다크.
 *
 * 텍스트는 expression 을 **리터럴 그대로**(평가 안 함) textContent 로 넣는다 → 따옴표/줄바꿈/`<` 안전.
 * 렌더는 document.body 의 공용 노드 1개(.ldk-tooltip, position:fixed) 를 재사용 — 호스트 DOM 을 건드리지 않아
 * SVG 호스트·overflow 클리핑·position:relative 강제 문제가 없다(종전 구현은 호스트 안에 insertAdjacentHTML).
 * 표시는 즉시(지연 없음, opacity 80ms 만 — style.css .ldk-tooltip), 화면 밖이면 반대편으로 flip.
 * 숨김: mouseleave / blur / 스크롤(capture) / Escape / 디렉티브 cleanup.
 */
export default function tooltipPlugin(Alpine) {
    const GAP = 8;        // 호스트와 툴팁 사이(화살표 포함)
    const EDGE = 8;       // 뷰포트 가장자리 최소 여백
    const ARROW_INSET = 16; // start/end 정렬 시 툴팁 끝에서 화살표 중심까지 거리(모서리 radius 와 겹치지 않게)
    let node = null;      // 공용 툴팁 노드
    let activeEl = null;  // 현재 툴팁을 띄운 호스트

    function ensureNode() {
        if (node) return node;
        node = document.createElement('div');
        node.className = 'ldk-tooltip';
        node.setAttribute('role', 'tooltip');
        node.id = 'ldk-tooltip';
        document.body.appendChild(node);
        // 스크롤/리사이즈/Escape 시 숨김 — 좌표가 어긋난 채 떠 있지 않게
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        window.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });
        return node;
    }

    function hide() {
        if (!node || !activeEl) return;
        node.classList.remove('is-visible');
        delete node.dataset.variant; // 변형 색이 다음 툴팁으로 새지 않게
        activeEl = null;
    }

    function opposite(pos) {
        return { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }[pos];
    }

    /** top/bottom 에서 가로 위치 — align: null(중앙)/'start'(오른쪽으로 펼침)/'end'(왼쪽으로 펼침). */
    function horizontalLeft(r, tw, align) {
        const cx = r.left + r.width / 2;
        if (align === 'start') return cx - ARROW_INSET;
        if (align === 'end') return cx + ARROW_INSET - tw;
        return cx - tw / 2;
    }

    function place(el, pos, arrow, align) {
        const n = ensureNode();
        const r = el.getBoundingClientRect();
        n.dataset.pos = pos;
        n.classList.toggle('no-arrow', !arrow);
        // 크기 측정을 위해 먼저 보이게(opacity 는 CSS 트랜지션)
        n.style.left = '0px';
        n.style.top = '0px';
        n.classList.add('is-visible');
        const tw = n.offsetWidth;
        const th = n.offsetHeight;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // 화면 밖이면 반대편으로 1회 flip
        const fits = {
            top: r.top - GAP - th >= EDGE,
            bottom: r.bottom + GAP + th <= vh - EDGE,
            left: r.left - GAP - tw >= EDGE,
            right: r.right + GAP + tw <= vw - EDGE,
        };
        if (!fits[pos] && fits[opposite(pos)]) {
            pos = opposite(pos);
            n.dataset.pos = pos;
        }

        let left, top;
        switch (pos) {
            case 'bottom':
                left = horizontalLeft(r, tw, align);
                top = r.bottom + GAP;
                break;
            case 'left':
                left = r.left - GAP - tw;
                top = r.top + r.height / 2 - th / 2;
                break;
            case 'right':
                left = r.right + GAP;
                top = r.top + r.height / 2 - th / 2;
                break;
            default: // top
                left = horizontalLeft(r, tw, align);
                top = r.top - GAP - th;
        }
        // 뷰포트 안으로 클램프(화살표는 호스트 중심을 가리키도록 offset 보정)
        const clampedLeft = Math.min(Math.max(left, EDGE), vw - EDGE - tw);
        const clampedTop = Math.min(Math.max(top, EDGE), vh - EDGE - th);
        n.style.left = clampedLeft + 'px';
        n.style.top = clampedTop + 'px';
        if (pos === 'top' || pos === 'bottom') {
            n.style.setProperty('--arrow-x', (r.left + r.width / 2 - clampedLeft) + 'px');
            n.style.removeProperty('--arrow-y');
        } else {
            n.style.setProperty('--arrow-y', (r.top + r.height / 2 - clampedTop) + 'px');
            n.style.removeProperty('--arrow-x');
        }
    }

    Alpine.directive('tooltip', (el, { modifiers, expression }, { cleanup }) => {
        const text = expression == null ? '' : String(expression);
        const arrow = !modifiers.includes('noarrow');
        let position = 'top';
        for (const p of ['top', 'bottom', 'left', 'right']) {
            if (modifiers.includes(p)) { position = p; break; }
        }
        // 정렬(top/bottom 전용) — left/right 위치에서는 무시
        let align = null;
        for (const a of ['start', 'end']) {
            if (modifiers.includes(a)) { align = a; break; }
        }
        // 색 변형(2026-10-13) — 공용 노드에 data-variant 로 전달, CSS 는 .ldk-tooltip[data-variant="…"] 토큰 재정의.
        // 현재 'tonal'(블루 tonal 바탕) 1종. 호스트 CSS 변수는 body 의 공용 노드로 상속되지 않아 모디파이어로 받는다.
        let variant = null;
        for (const v of ['tonal']) {
            if (modifiers.includes(v)) { variant = v; break; }
        }

        const show = () => {
            if (!text) return;
            const n = ensureNode();
            n.textContent = text; // 리터럴 — HTML 해석 없음
            if (variant) n.dataset.variant = variant; else delete n.dataset.variant;
            activeEl = el;
            place(el, position, arrow, align);
        };
        const hideIfMine = () => { if (activeEl === el) hide(); };

        el.addEventListener('mouseenter', show);
        el.addEventListener('mouseleave', hideIfMine);
        el.addEventListener('focus', show);
        el.addEventListener('blur', hideIfMine);

        // 접근성
        el.setAttribute('aria-describedby', 'ldk-tooltip');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');

        cleanup(() => {
            hideIfMine();
            el.removeEventListener('mouseenter', show);
            el.removeEventListener('mouseleave', hideIfMine);
            el.removeEventListener('focus', show);
            el.removeEventListener('blur', hideIfMine);
        });
    });
}
